import { clone } from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import { findDOMNode } from "react-dom";

import { asDragSource } from "../../components/dragAndDrop/DraggableComponent";
import { TREE_VIEW } from "../../constants/identifiers";
import { PropTypeShapes } from "../../types";
import { calcTextSize } from "../../utils/calcTextSize";
import { chooseValueForIos } from "../../utils/codeSplit";
import { getModifierKeysForEvent } from "../../utils/getModifierKeys";
import hasAncestorOfClass, { hasAncestorOfAnyClasses } from "../../utils/hasAncestorOfClass";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import preprocessorQuery from "../../utils/preprocessorQuery";
import { withCharacterContext } from "../character/CharacterContext";
import FormEditableLabel from "../form/FormEditableLabel";
import FormRadio from "../form/FormRadio";
import Icon from "../interface/Icon";
import ListItemView from "../listView/ListItemView";

const isIos = preprocessorQuery.isIos();

const clickWasIcon = e => hasAncestorOfAnyClasses(e.target, ["collapseArrow", "statusIcon"]);

const dragCallbacks = {
  beginDrag: _props => {
    // TODO: Do we want to set isDragging or similar...?
  },

  canDrag: (props, dragSource, e) => {
    return dragSource.component.canDrag(e);
  },

  getDragSourceParams: props => {
    return {
      itemId: props.id,
      itemType: props.type,
      sourceView: props.viewId,
      sourceViewType: TREE_VIEW,
      indentLevel: props.indentLevel,
      children: props.itemChildren
    };
  },

  getPreviewElement: props => renderItemPreview(props)
};

const getInitialEditingStatus = (autoEditWhenBlank, isSelected, label, isPreview) => {
  let result = false;
  //#!if iOS !== true
  result = !isPreview && autoEditWhenBlank && isSelected && (!label || label === getLabelWithUntitled());
  //#!endif
  return result;
};

const getMiddleElement = (useProps, onChangeEditing, getTextSizeFunction, isPreview) => {
  const untitledText = getLabelWithUntitled();
  return useProps.onLabelChange ? (
    <FormEditableLabel
      disabled={useProps.disabled}
      value={useProps.label}
      displayValue={getLabelWithUntitled(useProps.label)}
      placeholder={useProps.placeholder ? useProps.placeholder : untitledText}
      multiline={true}
      allowNewLine={false}
      onChange={useProps.onLabelChange}
      onChangeEditing={onChangeEditing}
      getSizeFunction={getTextSizeFunction}
      isEditing={getInitialEditingStatus(useProps.autoEditWhenBlank, useProps.isSelected, useProps.label, isPreview)}
    />
  ) : (
    <span>{useProps.label}</span>
  );
};

const getRightElements = (useProps, collapseArrow, dragHandle) => {
  const {
    id,
    statusIcon,
    statusColor,
    statusLabel,
    statusIconIdentifier,
    onStatusClick,
    pulsateIconId,
    isEditMode
  } = useProps;
  const rightLast = isEditMode ? (
    dragHandle
  ) : statusIcon ? (
    <Icon
      className="statusIcon"
      icon={statusIcon}
      color={statusColor}
      tooltip={statusLabel}
      pulse={pulsateIconId && pulsateIconId === statusIconIdentifier}
      onClick={
        onStatusClick
          ? e => {
            onStatusClick(id, getModifierKeysForEvent(e));
          }
          : null
      }
    />
  ) : null;
  return (
    <div className="treeItemRightElementsContainer">
      {chooseValueForIos(collapseArrow, null)}
      {rightLast}
    </div>
  );
};

const getItemOnClick = (onClick, id) => {
  return onClick ? e => (clickWasIcon(e) ? null : onClick(id, getModifierKeysForEvent(e))) : null;
};

const renderItem = (useProps, isPreview, onChangeEditing, getTextSizeFunction) => {
  const {
    indentLevel,
    isSelected,
    labelColor,
    onClick,
    onDoubleClick,
    onLeftIconClick,
    id,
    itemChildren,
    isExpanded,
    onToggle,
    icon,
    iconColor,
    isDragging,
    isDroppingOn,
    isDroppingAbove,
    isDroppingBelow,
    noDropIndent,
    isDroppingGroup,
    width,
    viewId,
    isEditMode
  } = useProps;

  const style = {
    width: isPreview ? `${width}px` : `auto`
  };

  const childrenStyle = {
    opacity: isDragging ? 0 : 1
  };

  const hasChildren = Boolean(itemChildren && itemChildren.size > 0);

  const classNames = [
    "treeItem",
    isEditMode ? "editMode" : "",
    `indent${indentLevel > 6 ? "Deep" : indentLevel}`,
    isSelected ? "selected" : "",
    labelColor ? labelColor : "",
    isDroppingOn ? "dropOn" : "",
    isDroppingAbove ? "dropAbove" : "",
    isDroppingBelow ? "dropBelow" : "",
    isDroppingGroup ? "dropGroup" : "",
    noDropIndent ? "noDropIndent" : "",
    isDragging ? "dragging" : ""
  ];

  let radio = null;
  if (isEditMode) {
    radio = <FormRadio isChecked={isSelected} onChange={() => {}} />;
  }

  const dragHandle =
    isIos && useProps.canDrag ? <Icon className="dragHandle" icon="dragHandle" color="iconMid" /> : null;

  let collapseArrow = (
    <Icon
      icon={hasChildren ? (isExpanded ? "downArrow" : "rightArrow") : ""}
      className="collapseArrow"
      color="iconMono"
      onClick={hasChildren && onToggle ? () => onToggle(id, !isExpanded) : null}
      canFocus={false}
    />
  );
  return (
    <div
      style={style}
      className={classNames.join(" ")}
      onClick={getItemOnClick(onClick, id)}
      onDoubleClick={onDoubleClick ? onDoubleClick : null}
    >
      <div className="treeItemChildren" style={childrenStyle}>
        {chooseValueForIos(null, collapseArrow)}
        <ListItemView
          leftElements={
            isSelected && isEditMode ? (
              radio
            ) : icon ? (
              <Icon icon={icon} color={iconColor} className={icon !== "circle-f" ? "large" : "mediumDot"} />
            ) : null
          }
          onLeftElementsClick={onLeftIconClick ? () => onLeftIconClick(id, viewId) : null}
          middleElements={getMiddleElement(useProps, onChangeEditing, getTextSizeFunction, isPreview)}
          rightElements={getRightElements(useProps, collapseArrow, dragHandle)}
          isSelected={isSelected}
        />
      </div>
    </div>
  );
};

const renderItemPreview = useProps => {
  const cloneProps = clone(useProps);
  return renderItem(cloneProps, true);
};

class TreeItemView extends PureComponent {
  constructor(props, context) {
    super(props, context);

    this.onChangeEditing = this.onChangeEditing.bind(this);
    this.getTextSizeFunction = this.getTextSizeFunction.bind(this);

    this._domNode = null;
    this._isEditing = false;
  }

  componentDidMount() {
    this._domNode = findDOMNode(this);
    if (this.props.onReportDimensions) {
      const rect = this._domNode.getBoundingClientRect();
      this.props.onReportDimensions(this.props.id, rect);
    }
    this._isEditing = getInitialEditingStatus(this.props.autoEditWhenBlank, this.props.isSelected, this.props.label);
  }

  componentDidUpdate() {
    if (this.props.onReportDimensions) {
      const rect = this._domNode.getBoundingClientRect();
      this.props.onReportDimensions(this.props.id, rect);
    }
  }

  componentWillUnmount() {
    // Should report to remove itself in the dimensions
    if (this.props.onReportDimensions) {
      this.props.onReportDimensions(this.props.id, null);
    }
  }

  canDrag(e) {
    // Only drag in iOS if they targetted the drag handle
    if (isIos && this.props.isEditMode) {
      const clickWasDragHandle = hasAncestorOfClass(e.target, "dragHandle");
      if (!clickWasDragHandle) {
        return false;
      }
    }

    // Disable the drag and drop when editing inside the node
    // The drag and drop may impact on the click event and focusing.
    return !this._isEditing && this.props.canDrag;
  }

  onChangeEditing(value) {
    this._isEditing = value;
  }

  getTextSizeFunction(text, maxWidth) {
    const result = calcTextSize(
      text,
      this.props.characterContext.getGlobalFontSize(),
      this.props.characterContext.getWidthMap(),
      maxWidth
    ).size;
    return result;
  }

  render() {
    return renderItem(this.props, false, this.onChangeEditing, this.getTextSizeFunction);
  }
}

TreeItemView.propTypes = {
  indentLevel: PropTypes.number.isRequired,
  id: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  type: PropTypes.string,
  label: PropTypes.string.isRequired,
  onLabelChange: PropTypes.func,
  canDrag: PropTypes.bool,
  icon: PropTypes.string,
  statusIcon: PropTypes.string,
  labelColor: PropTypes.string,
  iconColor: PropTypes.string,
  statusColor: PropTypes.string,
  statusLabel: PropTypes.string,
  statusIconIdentifier: PropTypes.string,
  itemChildren: PropTypeShapes.treeViewItemList,
  isExpanded: PropTypes.bool,
  isSelected: PropTypes.bool.isRequired,
  isDroppingOn: PropTypes.bool,
  isDroppingAbove: PropTypes.bool,
  isDroppingBelow: PropTypes.bool,
  isDroppingGroup: PropTypes.bool,
  noDropIndent: PropTypes.bool,
  onToggle: PropTypes.func,
  onClick: PropTypes.func,
  onDoubleClick: PropTypes.func,
  onLeftIconClick: PropTypes.func,
  onStatusClick: PropTypes.func,
  viewId: PropTypes.string.isRequired,
  onReportDimensions: PropTypes.func,
  width: PropTypes.number.isRequired,
  autoEditWhenBlank: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  isEditMode: PropTypes.bool,
  placeholder: PropTypes.string,

  pulsateIconId: PropTypes.string,

  // From asDragSource
  isDragging: PropTypes.bool,
  dragIsFromSameSource: PropTypes.bool,

  // From character context
  characterContext: PropTypes.object.isRequired
};

export default withCharacterContext(asDragSource(TreeItemView, TREE_VIEW, dragCallbacks));
